import { createFileRoute } from "@tanstack/react-router";
import type { CatalogIndexBy } from "@/api/types";
import { GraphEntrance } from "@/components/graph/GraphEntrance";
import { GraphIntro } from "@/components/graph/GraphIntro";
import { QueryEntrance } from "@/components/query/QueryEntrance";
import { CatalogEntrance } from "@/components/catalog/CatalogEntrance";
import { INDEX_BY_VALUES } from "@/components/catalog/IndexingToggle";

/** Entrance modes per Part 2 of the visual identity reference. The
 * homepage renders one of these three views based on `?mode=`. Default
 * is `graph` — first-time anonymous visitors land on the graph
 * (revelation viz) so the architectural thesis lands in the first
 * 30 seconds. All three entrance renderings (graph / query / catalog)
 * are live.
 *
 * Both params stay optional in the schema so plain `<Link to="/">`
 * elsewhere in the app (header wordmark, 404 back-link, etc.) keep
 * compiling. Consumers default `mode` to `"graph"` at read-time. */
export type EntranceMode = "query" | "graph" | "catalog";

const ENTRANCE_MODES: readonly EntranceMode[] = ["query", "graph", "catalog"] as const;

interface HomeSearchParams {
  mode?: EntranceMode;
  entity?: string;
  /** Catalog indexing scheme (Part 5). Only meaningful when mode=catalog. */
  index_by?: CatalogIndexBy;
}

export const Route = createFileRoute("/")({
  component: HomePage,
  validateSearch(raw: Record<string, unknown>): HomeSearchParams {
    const mode = ENTRANCE_MODES.find((m) => m === raw.mode);
    const entity = typeof raw.entity === "string" ? raw.entity : undefined;
    const indexBy = INDEX_BY_VALUES.find((v) => v === raw.index_by);
    const params: HomeSearchParams = {};
    if (mode) params.mode = mode;
    if (entity) params.entity = entity;
    if (indexBy) params.index_by = indexBy;
    return params;
  },
});

function HomePage() {
  const { mode, entity, index_by } = Route.useSearch();

  if (mode === "graph") {
    return (
      <div className="container mx-auto px-4 py-12">
        <GraphEntrance activeEntityId={entity ?? null} />
        <GraphIntro />
      </div>
    );
  }

  if (mode === "query") {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-12">
        <QueryEntrance />
      </div>
    );
  }

  if (mode === "catalog") {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-12">
        <CatalogEntrance indexBy={index_by ?? "institution"} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
      <GraphEntrance activeEntityId={entity ?? null} />
      <GraphIntro />
    </div>
  );
}
